import { useState } from 'react';

interface DonutChartProps {
  data: { label: string; value: number; color?: string }[];
  size?: number;
  centerLabel?: string;
  centerValue?: string;
}

export default function DonutChart({ data, size = 200, centerLabel, centerValue }: DonutChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const radius = size / 2 - 10;
  const innerRadius = radius * 0.6;
  const center = size / 2;

  const colors = [
    '#0d9488', '#0ea5e9', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#14b8a6', '#6366f1', '#f97316', '#06b6d4',
  ];

  let currentAngle = -90;

  function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  function describeArc(cx: number, cy: number, outerR: number, innerR: number, startAngle: number, endAngle: number) {
    const outerStart = polarToCartesian(cx, cy, outerR, endAngle);
    const outerEnd = polarToCartesian(cx, cy, outerR, startAngle);
    const innerStart = polarToCartesian(cx, cy, innerR, startAngle);
    const innerEnd = polarToCartesian(cx, cy, innerR, endAngle);
    const largeArc = endAngle - startAngle > 180 ? 1 : 0;

    return [
      `M ${outerStart.x} ${outerStart.y}`,
      `A ${outerR} ${outerR} 0 ${largeArc} 0 ${outerEnd.x} ${outerEnd.y}`,
      `L ${innerStart.x} ${innerStart.y}`,
      `A ${innerR} ${innerR} 0 ${largeArc} 1 ${innerEnd.x} ${innerEnd.y}`,
      'Z',
    ].join(' ');
  }

  const segments = data.map((item, i) => {
    const percentage = total > 0 ? (item.value / total) * 100 : 0;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + Math.max(angle, 0.5);
    currentAngle = endAngle;

    return {
      ...item,
      percentage,
      startAngle,
      endAngle,
      color: item.color || colors[i % colors.length],
      index: i,
    };
  });

  return (
    <div className="flex items-center gap-6">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((seg) => (
          <path
            key={seg.index}
            d={describeArc(center, center, hoveredIndex === seg.index ? radius + 4 : radius, innerRadius, seg.startAngle, seg.endAngle)}
            fill={seg.color}
            opacity={hoveredIndex !== null && hoveredIndex !== seg.index ? 0.5 : 1}
            onMouseEnter={() => setHoveredIndex(seg.index)}
            onMouseLeave={() => setHoveredIndex(null)}
            style={{ transition: 'all 0.2s ease', cursor: 'pointer' }}
          />
        ))}
        {centerLabel && (
          <>
            <text x={center} y={center - 6} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="11">{centerLabel}</text>
            <text x={center} y={center + 14} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="16" fontWeight="700">{centerValue}</text>
          </>
        )}
      </svg>
      <div className="flex flex-col gap-2 min-w-[140px]">
        {segments.map((seg) => (
          <div
            key={seg.index}
            className={`flex items-center gap-2 text-xs transition-opacity duration-200 ${hoveredIndex !== null && hoveredIndex !== seg.index ? 'opacity-40' : ''}`}
            onMouseEnter={() => setHoveredIndex(seg.index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
            <span className="text-white truncate">{seg.label}</span>
            <span className="text-white font-semibold ml-auto">{seg.percentage.toFixed(1)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
